import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SLOT_PAGE_DAYS, type Slot, type SlotsResponse } from "./contract";
import { addDays, dateKey, minKey } from "./dates";

/** `GET /public/v1/actions/{run_id}/slots` query: visitor-local dates, at most 14 days. */
export interface SlotRange {
  from: string;
  to: string;
  timezone: string;
}

export type FetchSlots = (range: SlotRange) => Promise<SlotsResponse>;

export interface SlotDay {
  date: string;
  slots: Slot[];
}

export interface UseSlotsOptions {
  fetchSlots: FetchSlots | undefined;
  /** Visitor time zone the days are grouped in. */
  timeZone: string;
  /** First date of the initial window; today in `timeZone` by default. */
  from?: string;
}

export interface UseSlotsResult {
  /** `error`: nothing could be loaded; a failed "more days" / day reload keeps the days (`partialError`). */
  status: "loading" | "ready" | "error";
  partialError: boolean;
  /** Only days with at least one open slot, ascending. */
  days: SlotDay[];
  loadingMore: boolean;
  /** Another window of days exists before the server's `latest` bookable date. */
  canLoadMore: boolean;
  /** The first open slot after an empty window. */
  nextAvailable: Slot | undefined;
  loadMore: () => void;
  /** Reloads the window starting at `date` ("Jump to Oct 14"). */
  jumpTo: (date: string) => void;
  /** Re-reads one day after `slot_unavailable`. */
  reloadDay: (date: string) => void;
  retry: () => void;
}

type Operation = { kind: "window"; from: string } | { kind: "more" } | { kind: "day"; date: string };

interface Loaded {
  days: Map<string, Slot[]>;
  loadedThrough: string;
  latest: string;
  nextAvailable: Slot | undefined;
}

function sortedDays(days: Map<string, Slot[]>): SlotDay[] {
  return [...days.entries()]
    .filter(([, slots]) => slots.length > 0)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([date, slots]) => ({ date, slots }));
}

function mergeDays(into: Map<string, Slot[]>, response: SlotsResponse): Map<string, Slot[]> {
  const merged = new Map(into);
  for (const day of response.days) merged.set(day.date, day.slots);
  return merged;
}

/** The loaded days after `response`: a window replaces them, more days are added, a day reload replaces one day. */
function applyResponse(operation: Operation, current: Loaded | null, range: SlotRange, response: SlotsResponse): Loaded {
  const base = operation.kind === "window" ? new Map<string, Slot[]>() : (current?.days ?? new Map<string, Slot[]>());
  const days = mergeDays(base, response);
  if (operation.kind === "day" && !response.days.some((day) => day.date === operation.date)) days.delete(operation.date);
  return {
    days,
    loadedThrough: operation.kind === "day" ? (current?.loadedThrough ?? range.to) : range.to,
    latest: response.latest,
    nextAvailable: sortedDays(days).length === 0 ? response.next_available : undefined,
  };
}

/**
 * Open slots of an offered booking, loaded a week at a time (plain hooks, injectable fetcher).
 * Stale answers (a newer window was requested meanwhile) are dropped.
 */
export function useSlots({ fetchSlots, timeZone, from }: UseSlotsOptions): UseSlotsResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [status, setStatus] = useState<UseSlotsResult["status"]>("loading");
  const [loadingMore, setLoadingMore] = useState(false);
  const [partialError, setPartialError] = useState(false);
  const fetchRef = useRef(fetchSlots);
  const loadedRef = useRef<Loaded | null>(null);
  const generation = useRef(0);
  const lastOperation = useRef<Operation | null>(null);
  useEffect(() => {
    fetchRef.current = fetchSlots;
    loadedRef.current = loaded;
  });

  const run = useCallback(
    async (operation: Operation) => {
      const fetcher = fetchRef.current;
      const current = loadedRef.current;
      if (!fetcher) return;
      if (operation.kind === "more" && !current) return;
      lastOperation.current = operation;
      if (operation.kind === "window") generation.current += 1;
      const ticket = generation.current;
      const range = rangeFor(operation, current, timeZone);
      if (!range) return;
      setPartialError(false);
      if (operation.kind === "window") {
        setStatus("loading");
        setLoadingMore(false);
      }
      if (operation.kind === "more") setLoadingMore(true);
      try {
        const response = await fetcher(range);
        if (ticket !== generation.current) return;
        setLoaded(applyResponse(operation, current, range, response));
        setStatus("ready");
      } catch {
        if (ticket !== generation.current) return;
        if (operation.kind === "window") setStatus("error");
        else setPartialError(true);
      } finally {
        if (operation.kind === "more" && ticket === generation.current) setLoadingMore(false);
      }
    },
    [timeZone],
  );

  const start = from ?? dateKey(new Date(), timeZone);
  useEffect(() => {
    if (!fetchRef.current) return undefined;
    void run({ kind: "window", from: start });
    return () => {
      generation.current += 1;
    };
  }, [run, start]);

  const loadMore = useCallback(() => {
    void run({ kind: "more" });
  }, [run]);
  const jumpTo = useCallback(
    (date: string) => {
      void run({ kind: "window", from: date });
    },
    [run],
  );
  const reloadDay = useCallback(
    (date: string) => {
      void run({ kind: "day", date });
    },
    [run],
  );
  const retry = useCallback(() => {
    const operation = lastOperation.current;
    if (operation) void run(operation);
  }, [run]);

  return useMemo(
    () => ({
      status,
      partialError,
      days: loaded ? sortedDays(loaded.days) : [],
      loadingMore,
      canLoadMore: loaded !== null && loaded.loadedThrough < loaded.latest,
      nextAvailable: loaded?.nextAvailable,
      loadMore,
      jumpTo,
      reloadDay,
      retry,
    }),
    [status, partialError, loaded, loadingMore, loadMore, jumpTo, reloadDay, retry],
  );
}

function rangeFor(operation: Operation, current: Loaded | null, timezone: string): SlotRange | null {
  switch (operation.kind) {
    case "window":
      return { from: operation.from, to: addDays(operation.from, SLOT_PAGE_DAYS - 1), timezone };
    case "day":
      return { from: operation.date, to: operation.date, timezone };
    case "more": {
      if (!current || current.loadedThrough >= current.latest) return null;
      const next = addDays(current.loadedThrough, 1);
      return { from: next, to: minKey(addDays(next, SLOT_PAGE_DAYS - 1), current.latest), timezone };
    }
  }
}
